import { Link } from 'react-router-dom';
import { 
  ArrowLeftRight, 
  Users, 
  Shield, 
  Banknote, 
  Wallet,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Building2,
  HandCoins,
  ArrowRight,
  BookOpen,
  Scale,
  Globe,
  Lock
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';

const P2PEducationPage = () => {
  const { t } = useLanguage();
  const p2p = t('p2p');

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar />
      
      {/* Academy Header - Wikipedia style, sticky below navbar */}
      <div className="sticky top-[80px] z-40">
        <div className="bg-gray-100 border-b border-gray-300">
          <div className="px-4 h-20 max-w-6xl mx-auto flex items-end pb-4">
            <Link 
              to="/education" 
              className="inline-flex items-center gap-3 text-gray-600 hover:text-gray-900 transition-colors group"
            >
              <ArrowRight size={20} className="rotate-180 group-hover:-translate-x-1 transition-transform" />
              <div className="w-8 h-8 rounded-full overflow-hidden ring-1 ring-gray-300 flex-shrink-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_realestate-fund-6/artifacts/qsxu12yq_1_LE_upscale_balanced_x4.webp"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <BookOpen size={20} className="text-gray-500 flex-shrink-0" />
              <span className="text-lg text-gray-700">
                AAC Academy
              </span>
              <span className="text-gray-400 text-lg">/</span>
              <span className="text-gray-900 text-lg font-medium">P2P Guide</span>
            </Link>
          </div>
        </div>
      </div>
      
      <main className="pt-4 pb-20">

        {/* Hero Section */}
        <section className="relative py-8 pb-4 overflow-hidden bg-gray-950">
          {/* Background image */}
          <img 
            src="https://customer-assets.emergentagent.com/job_realestate-fund-6/artifacts/qsxu12yq_1_LE_upscale_balanced_x4.webp"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Mobile: darker gradient, Desktop: lighter gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/70 to-gray-950 md:from-gray-950/50 md:via-gray-950/40 md:to-gray-950/60"></div>
          
          <div className="relative z-10 px-4 max-w-6xl mx-auto pt-28">
            <div className="text-center mb-6">
            {/* Icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-green-500/20 border border-cyan-500/30 mb-6">
              <ArrowLeftRight size={36} className="text-cyan-400" />
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              {p2p?.hero?.title || '¿Qué es P2P?'}
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-3">
              {p2p?.hero?.subtitle || 'Intercambia criptomonedas y efectivo directamente con otras personas.'}
            </p>
            <p className="text-lg text-cyan-400 font-medium">
              {p2p?.hero?.tagline || 'Sin bancos. Sin KYC obligatorio. Sin límites.'}
            </p>
          </div>

          {/* Quick Definition Card */}
          <div className="bg-gradient-to-br from-cyan-900/30 to-gray-900/90 border border-cyan-500/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="bg-cyan-500/20 p-3 rounded-xl flex-shrink-0">
                <BookOpen size={24} className="text-cyan-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-cyan-400 mb-3">{p2p?.definition?.title || 'Definición Rápida'}</h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  {p2p?.definition?.text || 'P2P (Peer-to-Peer) significa "de persona a persona". Es la forma más antigua y directa de intercambiar valor: tú me das euros, yo te doy Bitcoin. Sin bancos, sin intermediarios financieros, sin preguntas innecesarias.'}
                </p>
                <p className="text-gray-400 leading-relaxed">
                  {p2p?.definition?.text2 || 'El sistema de custodia (escrow) protege a ambas partes: puede ser una plataforma P2P, o un intermediario de confianza como nosotros que garantiza que el intercambio se complete correctamente.'}
                </p>
              </div>
            </div>
          </div>

          {/* Service Reminder Banner */}
          <div className="mt-8 mb-12 bg-gradient-to-r from-green-900/40 to-cyan-900/40 border border-green-500/30 rounded-2xl p-4 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="bg-green-500/20 p-2 rounded-lg">
                  <CheckCircle2 size={24} className="text-green-400" />
                </div>
                <p className="text-green-300 font-medium whitespace-pre-line">
                  {p2p?.serviceReminder?.text || 'Need to do P2P?\nWe handle it for you with complete security.'}
                </p>
              </div>
              {/* Buttons column: Services on top, WhatsApp/Telegram below */}
              <div className="flex flex-col gap-2">
                <Link to="/services#p2p" className="bg-cyan-500 hover:bg-cyan-400 text-black px-5 py-2 rounded-lg text-sm font-medium transition-colors text-center">
                  {p2p?.serviceReminder?.servicesBtn || 'Services'}
                </Link>
                <div className="flex gap-2">
                  <a href="https://wa.me/34633905071" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-400 text-black px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>
                  <a href="https://t.me/jimmoOTC" target="_blank" rel="noopener noreferrer" className="bg-[#0088cc] hover:bg-[#0099dd] text-white px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                    Telegram
                  </a>
                </div>
              </div>
            </div>
          </div>
          </div>
        </section>


        {/* Directions - Buy & Sell */}
        <section className="px-4 py-16 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">{p2p?.directions?.title || 'Dos Direcciones'}</h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              {p2p?.directions?.subtitle || 'P2P funciona en ambos sentidos según lo que necesites'}
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Fiat to Crypto */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-cyan-500/20 p-3 rounded-lg">
                      <Banknote size={24} className="text-cyan-400" />
                    </div>
                    <ArrowRight size={20} className="text-gray-500" />
                    <div className="bg-orange-500/20 p-3 rounded-lg">
                      <Wallet size={24} className="text-orange-400" />
                    </div>
                  </div>
                  <span className="bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full text-sm font-medium">
                    {p2p?.directions?.buy?.badge || 'COMPRAR'}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-3">{p2p?.directions?.buy?.title || 'Fiat → Crypto'}</h3>
                <p className="text-gray-400 mb-4">{p2p?.directions?.buy?.desc || 'Convierte tu dinero tradicional (EUR, USD) en criptomonedas (BTC, USDT, ETH...)'}</p>
                <div className="text-sm text-gray-500">
                  <strong className="text-gray-300">{p2p?.directions?.buy?.useCase?.title || 'Uso típico'}:</strong> {p2p?.directions?.buy?.useCase?.text || 'Entrar al mercado crypto, ahorrar en stablecoins, enviar remesas internacionales.'}
                </div>
              </div>

              {/* Crypto to Fiat */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-500/20 p-3 rounded-lg">
                      <Wallet size={24} className="text-orange-400" />
                    </div>
                    <ArrowRight size={20} className="text-gray-500" />
                    <div className="bg-green-500/20 p-3 rounded-lg">
                      <Banknote size={24} className="text-green-400" />
                    </div>
                  </div>
                  <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium">
                    {p2p?.directions?.sell?.badge || 'VENDER'}
                  </span>
                </div>
                <h3 className="text-xl font-bold mb-3">{p2p?.directions?.sell?.title || 'Crypto → Fiat'}</h3>
                <p className="text-gray-400 mb-4">{p2p?.directions?.sell?.desc || 'Convierte tus criptomonedas de vuelta a dinero tradicional en tu cuenta o efectivo.'}</p>
                <div className="text-sm text-gray-500">
                  <strong className="text-gray-300">{p2p?.directions?.sell?.useCase?.title || 'Uso típico'}:</strong> {p2p?.directions?.sell?.useCase?.text || 'Tomar ganancias, pagar gastos del día a día, liquidar posiciones.'}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Use Cases */}
        <section className="px-4 py-12 md:py-16">
          <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-3 md:mb-4">{p2p?.useCases?.title || '¿Para Qué Se Usa Realmente?'}</h2>
          <p className="text-gray-400 text-center mb-8 md:mb-12 max-w-2xl mx-auto text-sm md:text-base">
            {p2p?.useCases?.subtitle || 'Los casos de uso reales que nadie te cuenta'}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
            {[
              { 
                icon: Globe, 
                title: p2p?.useCases?.case1?.title || 'Remesas España ↔ Latinoamérica', 
                desc: p2p?.useCases?.case1?.desc || 'Envía euros a Venezuela, Colombia, Argentina... en minutos.',
                color: 'cyan'
              },
              { 
                icon: Scale, 
                title: p2p?.useCases?.case2?.title || 'Optimización Fiscal', 
                desc: p2p?.useCases?.case2?.desc || 'Elusión fiscal legal aprovechando crypto.',
                color: 'purple'
              },
              { 
                icon: Shield, 
                title: p2p?.useCases?.case3?.title || 'Privacidad Financiera', 
                desc: p2p?.useCases?.case3?.desc || 'Sin rastro bancario. Sin bloqueos ni preguntas.',
                color: 'green'
              },
              { 
                icon: Banknote, 
                title: p2p?.useCases?.case4?.title || 'Convertir Efectivo', 
                desc: p2p?.useCases?.case4?.desc || 'Efectivo ↔ crypto. Sin KYC, sin límites.',
                color: 'orange'
              },
              { 
                icon: ArrowLeftRight, 
                title: p2p?.useCases?.case5?.title || 'Mover Dinero Entre Países', 
                desc: p2p?.useCases?.case5?.desc || 'Convierte a crypto, viaja, reconvierte.',
                color: 'blue'
              },
              { 
                icon: Users, 
                title: p2p?.useCases?.case6?.title || 'Evitar Restricciones Bancarias', 
                desc: p2p?.useCases?.case6?.desc || 'Compra crypto directamente, sin intermediarios.',
                color: 'pink'
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4 md:p-6 hover:border-cyan-500/30 transition-colors">
                <div className="flex items-start gap-3 md:block">
                  <div className="bg-cyan-500/20 w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 md:mb-4">
                    <item.icon size={20} className="text-cyan-400 md:hidden" />
                    <item.icon size={24} className="text-cyan-400 hidden md:block" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm md:text-base mb-1 md:mb-2">{item.title}</h4>
                    <p className="text-xs md:text-sm text-gray-400">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Another Service Reminder */}
          <div className="mt-8 md:mt-12 text-center">
            <p className="text-gray-400 mb-4 text-sm md:text-base">{p2p?.useCases?.serviceNote || '¿Te identificas con alguno de estos casos? Podemos ayudarte.'}</p>
            <div className="flex justify-center gap-3 md:gap-4 flex-wrap">
              <Link to="/services#p2p" className="bg-cyan-500 hover:bg-cyan-400 text-black px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium text-sm md:text-base transition-colors">
                {p2p?.useCases?.servicesBtn || 'Ver Servicios P2P'}
              </Link>
              <a href="https://wa.me/34633905071" target="_blank" rel="noopener noreferrer" className="bg-green-500 hover:bg-green-400 text-black px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium flex items-center gap-2 text-sm md:text-base transition-colors">
                <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
            </div>
          </div>
          </div>
        </section>

        {/* Payment Methods - Banking vs Cash */}
        <section className="px-4 py-16 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">{p2p?.methods?.title || 'Métodos de Pago'}</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            {p2p?.methods?.subtitle || 'Dos categorías principales: bancario y efectivo físico'}
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Banking */}
            <div className="bg-gradient-to-br from-blue-900/20 to-gray-900 border border-blue-500/20 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-blue-500/20 p-4 rounded-xl">
                  <Building2 size={32} className="text-blue-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-blue-400">{p2p?.methods?.banking?.title || 'Bancario'}</h3>
                  <p className="text-gray-400 text-sm">{p2p?.methods?.banking?.subtitle || 'Transferencias digitales'}</p>
                </div>
              </div>
              <ul className="space-y-3">
                {(p2p?.methods?.banking?.items || [
                  'Transferencia bancaria (SEPA, SWIFT)',
                  'Bizum, Revolut, Wise',
                  'PayPal, Skrill, Neteller',
                  'Pagos móviles locales'
                ]).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle2 size={18} className="text-blue-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 p-4 bg-blue-500/10 rounded-lg">
                <p className="text-sm text-blue-300">
                  <strong>{p2p?.methods?.banking?.note?.title || 'Ventaja'}:</strong> {p2p?.methods?.banking?.note?.text || 'Trazabilidad completa, ideal para montos grandes y cumplimiento fiscal.'}
                </p>
              </div>
            </div>

            {/* Cash */}
            <div className="bg-gradient-to-br from-green-900/20 to-gray-900 border border-green-500/20 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-green-500/20 p-4 rounded-xl">
                  <HandCoins size={32} className="text-green-400" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-green-400">{p2p?.methods?.cash?.title || 'Efectivo Físico'}</h3>
                  <p className="text-gray-400 text-sm">{p2p?.methods?.cash?.subtitle || 'Intercambio en persona'}</p>
                </div>
              </div>
              <ul className="space-y-3">
                {(p2p?.methods?.cash?.items || [
                  'Encuentro en persona (lugar público)',
                  'Depósito en efectivo en banco',
                  'Cajeros de crypto (ATMs)',
                  'Servicios de remesas'
                ]).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <CheckCircle2 size={18} className="text-green-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 p-4 bg-green-500/10 rounded-lg">
                <p className="text-sm text-green-300">
                  <strong>{p2p?.methods?.cash?.note?.title || 'Ventaja'}:</strong> {p2p?.methods?.cash?.note?.text || 'Mayor privacidad, sin necesidad de cuenta bancaria, ideal para pequeñas cantidades.'}
                </p>
              </div>
            </div>
          </div>
          </div>
        </section>

        {/* How it Works - Cash */}
        <section className="px-4 pt-8 md:pt-12 pb-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">{p2p?.howItWorks?.title || '¿Cómo Funciona?'}</h2>
            
            {/* Cash Method */}
            <div className="mb-12 md:mb-16">
              <div className="flex items-center justify-center gap-3 mb-6 md:mb-8">
                <div className="bg-green-500/20 p-2 rounded-lg">
                  <HandCoins size={20} className="text-green-400" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-green-400">{p2p?.howItWorks?.cashTitle || 'Método Efectivo (En Persona)'}</h3>
              </div>

              {/* Mobile: Compact layout with icons */}
              <div className="md:hidden space-y-2">
                {[
                  { icon: Users, title: p2p?.howItWorks?.cash?.step1?.title || 'Contacto', desc: p2p?.howItWorks?.cash?.step1?.desc || 'Encuentras a alguien que quiere comprar/vender.' },
                  { icon: Lock, title: p2p?.howItWorks?.cash?.step2?.title || 'Acuerdo', desc: p2p?.howItWorks?.cash?.step2?.desc || 'Acordáis precio, cantidad y lugar.' },
                  { icon: HandCoins, title: p2p?.howItWorks?.cash?.step3?.title || 'Intercambio', desc: p2p?.howItWorks?.cash?.step3?.desc || 'Efectivo ↔ crypto.' },
                  { icon: CheckCircle2, title: p2p?.howItWorks?.cash?.step4?.title || 'Verificación', desc: p2p?.howItWorks?.cash?.step4?.desc || 'Confirmar tx en blockchain.' },
                  { icon: Wallet, title: p2p?.howItWorks?.cash?.step5?.title || 'Completado', desc: p2p?.howItWorks?.cash?.step5?.desc || 'Sin rastro, sin KYC.' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-900 border border-green-500/20 rounded-lg p-3">
                    <div className="bg-green-500/20 w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0">
                      <step.icon size={18} className="text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-sm text-white flex items-center gap-2">
                        <span className="text-green-400 text-xs">{i + 1}.</span> {step.title}
                      </h4>
                      <p className="text-xs text-gray-400">{step.desc}</p>
                    </div>
                    {i < 4 && <ArrowRight size={14} className="text-green-500/50 flex-shrink-0" />}
                  </div>
                ))}
              </div>

              {/* Desktop: Full layout with icons */}
              <div className="hidden md:flex flex-row items-stretch justify-center gap-0">
                {[
                  { icon: Users, title: p2p?.howItWorks?.cash?.step1?.title || 'Contacto', desc: p2p?.howItWorks?.cash?.step1?.desc || 'Encuentras a alguien que quiere comprar/vender.' },
                  { icon: Lock, title: p2p?.howItWorks?.cash?.step2?.title || 'Acuerdo', desc: p2p?.howItWorks?.cash?.step2?.desc || 'Acordáis precio, cantidad y lugar (público y seguro).' },
                  { icon: HandCoins, title: p2p?.howItWorks?.cash?.step3?.title || 'Intercambio', desc: p2p?.howItWorks?.cash?.step3?.desc || 'Uno entrega efectivo, el otro envía crypto.' },
                  { icon: CheckCircle2, title: p2p?.howItWorks?.cash?.step4?.title || 'Verificación', desc: p2p?.howItWorks?.cash?.step4?.desc || 'Se confirma la tx en blockchain.' },
                  { icon: Wallet, title: p2p?.howItWorks?.cash?.step5?.title || 'Completado', desc: p2p?.howItWorks?.cash?.step5?.desc || 'Sin rastro bancario, sin KYC.' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center">
                    <div className="bg-gray-900 border border-green-500/20 rounded-xl p-5 w-48">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-green-400 text-sm font-bold">{i + 1}.</span>
                        <div className="bg-green-500/20 w-10 h-10 rounded-lg flex items-center justify-center">
                          <step.icon size={20} className="text-green-400" />
                        </div>
                      </div>
                      <h4 className="font-bold text-sm mb-1">{step.title}</h4>
                      <p className="text-xs text-gray-400">{step.desc}</p>
                    </div>
                    {i < 4 && (
                      <div className="flex items-center justify-center w-8">
                        <ArrowRight size={16} className="text-green-500/50" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Banking Method */}
            <div>
              <div className="flex items-center justify-center gap-3 mb-6 md:mb-8">
                <div className="bg-blue-500/20 p-2 rounded-lg">
                  <Building2 size={20} className="text-blue-400" />
                </div>
                <h3 className="text-lg md:text-xl font-semibold text-blue-400">{p2p?.howItWorks?.bankTitle || 'Método Bancario (Online)'}</h3>
              </div>

              {/* Mobile: Compact horizontal layout */}
              <div className="md:hidden space-y-2">
                {[
                  { icon: Users, title: p2p?.howItWorks?.bank?.step1?.title || 'Oferta', desc: p2p?.howItWorks?.bank?.step1?.desc || 'Eliges oferta P2P con buen rating.' },
                  { icon: Lock, title: p2p?.howItWorks?.bank?.step2?.title || 'Escrow', desc: p2p?.howItWorks?.bank?.step2?.desc || 'Crypto en custodia.' },
                  { icon: Banknote, title: p2p?.howItWorks?.bank?.step3?.title || 'Transferencia', desc: p2p?.howItWorks?.bank?.step3?.desc || 'Pagas al vendedor.' },
                  { icon: CheckCircle2, title: p2p?.howItWorks?.bank?.step4?.title || 'Confirmación', desc: p2p?.howItWorks?.bank?.step4?.desc || 'Vendedor confirma pago.' },
                  { icon: Wallet, title: p2p?.howItWorks?.bank?.step5?.title || 'Liberación', desc: p2p?.howItWorks?.bank?.step5?.desc || 'Crypto a tu wallet.' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 bg-gray-900 border border-blue-500/20 rounded-lg p-3">
                    <div className="bg-blue-500/20 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-400 font-bold text-sm">{i + 1}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-sm text-white">{step.title}</h4>
                      <p className="text-xs text-gray-400">{step.desc}</p>
                    </div>
                    {i < 4 && <ArrowRight size={14} className="text-blue-500/50 flex-shrink-0" />}
                  </div>
                ))}
              </div>

              {/* Desktop: Full layout with icons */}
              <div className="hidden md:flex flex-row items-stretch justify-center gap-0">
                {[
                  { icon: Users, title: p2p?.howItWorks?.bank?.step1?.title || 'Oferta', desc: p2p?.howItWorks?.bank?.step1?.desc || 'Eliges oferta en plataforma P2P con buen rating.' },
                  { icon: Lock, title: p2p?.howItWorks?.bank?.step2?.title || 'Escrow', desc: p2p?.howItWorks?.bank?.step2?.desc || 'La crypto se bloquea en custodia de la plataforma.' },
                  { icon: Banknote, title: p2p?.howItWorks?.bank?.step3?.title || 'Transferencia', desc: p2p?.howItWorks?.bank?.step3?.desc || 'Haces transferencia/Bizum al vendedor.' },
                  { icon: CheckCircle2, title: p2p?.howItWorks?.bank?.step4?.title || 'Confirmación', desc: p2p?.howItWorks?.bank?.step4?.desc || 'El vendedor confirma recepción del pago.' },
                  { icon: Wallet, title: p2p?.howItWorks?.bank?.step5?.title || 'Liberación', desc: p2p?.howItWorks?.bank?.step5?.desc || 'La plataforma libera la crypto a tu wallet.' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center">
                    <div className="bg-gray-900 border border-blue-500/20 rounded-xl p-5 w-48">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-blue-400 text-sm font-bold">{i + 1}.</span>
                        <div className="bg-blue-500/20 w-10 h-10 rounded-lg flex items-center justify-center">
                          <step.icon size={20} className="text-blue-400" />
                        </div>
                      </div>
                      <h4 className="font-bold text-sm mb-1">{step.title}</h4>
                      <p className="text-xs text-gray-400">{step.desc}</p>
                    </div>
                    {i < 4 && (
                      <div className="flex items-center justify-center w-8">
                        <ArrowRight size={16} className="text-blue-500/50" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* Taxes Section */}
        <section className="px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Scale size={32} className="text-yellow-400" />
              <h2 className="text-3xl font-bold">{p2p?.taxes?.title || 'Elusión vs Evasión'}</h2>
            </div>
            <p className="text-gray-400 text-center mb-8 max-w-2xl mx-auto">
              {p2p?.taxes?.subtitle || 'Entender la diferencia es crucial'}
            </p>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-8">
              <div className="space-y-6">
                <div>
                  <h4 className="font-bold text-green-400 mb-2">{p2p?.taxes?.point1?.title || '✓ Elusión Fiscal (Legal)'}</h4>
                  <p className="text-gray-300">{p2p?.taxes?.point1?.text || 'Optimizar tu situación fiscal aprovechando las herramientas legales disponibles. Usar crypto para mover valor entre jurisdicciones, aprovechar diferentes tratamientos fiscales, diferir impuestos... Todo esto es perfectamente legal si se hace correctamente.'}</p>
                </div>
                <div>
                  <h4 className="font-bold text-red-400 mb-2">{p2p?.taxes?.point2?.title || '✗ Evasión Fiscal (Ilegal)'}</h4>
                  <p className="text-gray-300">{p2p?.taxes?.point2?.text || 'Ocultar ingresos o patrimonio a Hacienda. No declarar ganancias que legalmente debes declarar. Esto es un delito y las autoridades tienen cada vez más herramientas para detectarlo.'}</p>
                </div>
                <div>
                  <h4 className="font-bold text-yellow-400 mb-2">{p2p?.taxes?.point3?.title || '¿Qué pasa con el P2P en efectivo?'}</h4>
                  <p className="text-gray-300">{p2p?.taxes?.point3?.text || 'Las transacciones P2P en efectivo no dejan rastro bancario, pero eso no significa que sean ilegales. Lo importante es la intención y el cumplimiento de tus obligaciones fiscales según tu situación personal.'}</p>
                </div>
              </div>

              <div className="mt-8 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                <p className="text-sm text-yellow-200 flex items-start gap-2">
                  <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                  <span>{p2p?.taxes?.disclaimer || 'Cada persona tiene una situación fiscal diferente. Lo que es legal para un no-residente puede no serlo para un residente. Consulta con un asesor fiscal especializado en crypto.'}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="px-4 py-16 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">{p2p?.proscons?.title || 'Ventajas y Riesgos'}</h2>

            <div className="grid md:grid-cols-2 gap-8">
            {/* Pros */}
            <div className="bg-green-500/5 border border-green-500/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-green-400 mb-6 flex items-center gap-2">
                <CheckCircle2 size={24} />
                {p2p?.proscons?.pros?.title || 'Ventajas'}
              </h3>
              <ul className="space-y-4">
                {(p2p?.proscons?.pros?.items || [
                  { title: 'Flexibilidad de pago', desc: 'Elige entre docenas de métodos de pago' },
                  { title: 'Mejores precios', desc: 'Negocia directamente, sin spreads ocultos' },
                  { title: 'Acceso global', desc: 'Opera con personas de cualquier país' },
                  { title: 'Mayor privacidad', desc: 'Menos KYC que exchanges centralizados' },
                  { title: 'Sin límites bancarios', desc: 'Evita restricciones de tu banco con crypto' }
                ]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-green-400 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-medium text-white">{item.title}</span>
                      <span className="text-gray-400"> — {item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cons */}
            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-red-400 mb-6 flex items-center gap-2">
                <XCircle size={24} />
                {p2p?.proscons?.cons?.title || 'Riesgos'}
              </h3>
              <ul className="space-y-4">
                {(p2p?.proscons?.cons?.items || [
                  { title: 'Estafas posibles', desc: 'Verifica siempre la reputación del trader' },
                  { title: 'Más lento', desc: 'Requiere esperar confirmación manual del pago' },
                  { title: 'Menor liquidez', desc: 'Puede no haber ofertas para grandes montos' },
                  { title: 'Disputas', desc: 'Posibles conflictos que requieren mediación' },
                  { title: 'Curva de aprendizaje', desc: 'Más complejo que comprar en un exchange' }
                ]).map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <XCircle size={18} className="text-red-400 flex-shrink-0 mt-1" />
                    <div>
                      <span className="font-medium text-white">{item.title}</span>
                      <span className="text-gray-400"> — {item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          </div>
        </section>

        {/* Safety Tips */}
        <section className="px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-4">{p2p?.safety?.title || 'Consejos de Seguridad'}</h2>
            <p className="text-gray-400 text-center mb-12">
              {p2p?.safety?.subtitle || 'Sigue estas reglas para operar de forma segura'}
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              {(p2p?.safety?.tips || [
                { icon: '🔍', text: 'Verifica la reputación y historial del trader antes de operar' },
                { icon: '🔒', text: 'Nunca liberes crypto hasta confirmar el pago en tu cuenta' },
                { icon: '💬', text: 'Usa solo el chat de la plataforma, no Telegram/WhatsApp externos' },
                { icon: '📸', text: 'Guarda capturas de pantalla de todas las conversaciones' },
                { icon: '⚠️', text: 'Desconfía de ofertas demasiado buenas para ser verdad' },
                { icon: '🏦', text: 'Verifica que el nombre del pagador coincide con el trader' },
                { icon: '🚫', text: 'Nunca aceptes pagos de terceros (posible lavado de dinero)' },
                { icon: '📊', text: 'Empieza con montos pequeños hasta ganar confianza' }
              ]).map((tip, i) => (
                <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-start gap-3">
                  <span className="text-2xl">{tip.icon}</span>
                  <p className="text-gray-300">{tip.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 py-16 bg-gray-900/50 text-center">
          <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-4">{p2p?.cta?.title || '¿Necesitas ayuda con P2P?'}</h2>
          <p className="text-gray-400 mb-8">
            {p2p?.cta?.subtitle || 'Nuestro equipo puede asesorarte en operaciones P2P de alto volumen con total seguridad.'}
          </p>
          <a 
            href="https://wa.me/34633905071" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-black font-semibold px-8 py-4 rounded-xl transition-colors"
          >
            {p2p?.cta?.button || 'Contactar por WhatsApp'}
            <ArrowRight size={20} />
          </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default P2PEducationPage;
